import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CloseIcon from "@mui/icons-material/Close";
import sfdaMedicine from "../../gql/sfdaMedicine";
import CustomFilterInput from "../../shared/CustomFilterInput";
import AppButton from "../../shared/AppButton";
import AppLoader from "../../components/AppLoader";
import { allSections } from "./components/SfdaFormSections";

function SfdaMedicineForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: medicineData, loading: fetching } = useQuery(
    sfdaMedicine.GET_SFDA_MEDICINE,
    {
      variables: { id },
      skip: !isEdit,
      fetchPolicy: "network-only",
    },
  );

  const item = medicineData?.getSfdaMedicine;

  const [createMedicine, { loading: creating }] = useMutation(
    sfdaMedicine.CREATE_SFDA_MEDICINE,
  );
  const [updateMedicine, { loading: updating }] = useMutation(
    sfdaMedicine.UPDATE_SFDA_MEDICINE,
  );

  const buildInitialValues = (data) => ({
    sfdaId: data?.sfdaId || 0,
    registerNumber: data?.registerNumber || "",
    registerYear: data?.registerYear || 0,
    gtin: data?.gtin || "",
    drugType: data?.drugType || "",
    subType: data?.subType || "",
    scientificName: data?.scientificName || "",
    tradeName: data?.tradeName || "",
    strength: data?.strength || "",
    strengthUnit: data?.strengthUnit || "",
    pharmaceuticalForm: data?.pharmaceuticalForm || "",
    administrationRoute: data?.administrationRoute || "",
    size: data?.size || "",
    sizeUnit: data?.sizeUnit || "",
    packageTypes: data?.packageTypes || "",
    legalStatus: data?.legalStatus || "",
    productControl: data?.productControl || "",
    publicPrice: data?.publicPrice || "",
    marketingCompany: data?.marketingCompany || "",
    atcCode1: data?.atcCode1 || "",
    atcCode2: data?.atcCode2 || "",
    packageSize: data?.packageSize || 0,
    shelfLife: data?.shelfLife || 0,
    storageConditions: data?.storageConditions || "",
    marketingStatus: data?.marketingStatus || "",
    authorizationStatus: data?.authorizationStatus || "",
    manufacturerName: data?.manufacturerName || "",
    manufacturerCountry: data?.manufacturerCountry || "",
    additionalManufacturer: data?.additionalManufacturer || "",
    additionalManufacturerCountry: data?.additionalManufacturerCountry || "",
    companyCountryEn: data?.companyCountryEn || "",
    companyCountryAr: data?.companyCountryAr || "",
    legalStatusEn: data?.legalStatusEn || "",
    legalStatusAr: data?.legalStatusAr || "",
    domainEn: data?.domainEn || "",
    domainAr: data?.domainAr || "",
    imageUrl: data?.imageUrl || "",
    distributionArea: data?.distributionArea || "",
    drugCombinationsStrengthValue: data?.drugCombinationsStrengthValue || "",
    drugCombinationsScientificName: data?.drugCombinationsScientificName || "",
    drugCombinationsStrengthUnit: data?.drugCombinationsStrengthUnit || "",
    descriptionCode: data?.descriptionCode || "",
    productControlAr: data?.productControlAr || "",
    agent: data?.agent || "",
    scientificCode: data?.scientificCode || "",
  });

  const validationSchema = Yup.object().shape({
    registerNumber: Yup.string().required(t("Register Number is required")),
    tradeName: Yup.string().required(t("Trade Name is required")),
  });

  const formik = useFormik({
    initialValues: buildInitialValues(item),
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      // Ensure supergitMasterCode, id, __typename are never present in input payload
      const { supergitMasterCode, id: _id, __typename, ...cleanValues } = values;
      const input = {
        ...cleanValues,
        sfdaId: parseInt(cleanValues.sfdaId) || 0,
        registerYear: parseInt(cleanValues.registerYear) || 0,
        packageSize: parseInt(cleanValues.packageSize) || 0,
        shelfLife: parseInt(cleanValues.shelfLife) || 0,
      };

      try {
        if (isEdit) {
          await updateMedicine({
            variables: { id, input },
          });
          toast.success(t("Medicine updated successfully"));
        } else {
          await createMedicine({
            variables: { input },
          });
          toast.success(t("Medicine created successfully"));
        }
        navigate("/sfda-medicine");
      } catch (error) {
        toast.error(error.message);
      }
    },
  });

  const renderSection = (title, fields) => (
    <div
      className="personal-data !shadow-none !border !border-gray-200 !p-3 !pb-3 !mb-2"
      key={title}
    >
      <div className="personal-title !mb-1.5">
        <div className="inventory-svg">
          <svg
            width="26"
            height="26"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="34" height="34" rx="8" fill="#F4F6F8" />
            <path
              d="M9.25 11H11.25V14H21.25V11H23.25V16H25.25V11C25.25 9.9 24.35 9 23.25 9H19.07C18.65 7.84 17.55 7 16.25 7C14.95 7 13.85 7.84 13.43 9H9.25C8.15 9 7.25 9.9 7.25 11V25C7.25 26.1 8.15 27 9.25 27H15.25V25H9.25V11ZM16.25 9C16.8 9 17.25 9.45 17.25 10C17.25 10.55 16.8 11 16.25 11C15.7 11 15.25 10.55 15.25 10C15.25 9.45 15.7 9 16.25 9Z"
              fill="#0070C2"
            />
            <path
              d="M25.25 17.5L19.76 23L16.75 20L15.25 21.5L19.76 26L26.75 19L25.25 17.5Z"
              fill="#0070C2"
            />
          </svg>
        </div>
        <div className="personal-head">
          <span>{t(title)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-1.5">
        {fields.map((field) => {
          const cleanLabel = field.label.replace("*", "").trim();
          const placeholderText = field.placeholder
            ? t(field.placeholder)
            : `${t("Enter")} ${t(cleanLabel)}`;
          return (
            <CustomFilterInput
              key={field.name}
              label={t(field.label)}
              name={field.name}
              type={field.type || "text"}
              placeholder={placeholderText}
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors[field.name]}
              touched={formik.touched[field.name]}
            />
          );
        })}
      </div>
    </div>
  );

  if (isEdit && fetching) {
    return <AppLoader />;
  }

  return (
    <div className="superGit_container">
      <div className="flex space-x-2 !mb-2">
        <div
          className="back flex items-center justify-center cursor-pointer transition-all duration-200 hover:opacity-80 active:scale-95"
          onClick={() => navigate("/sfda-medicine")}
          style={{
            backgroundColor: "#0070C2",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {i18n.language === "en" ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                fill="white"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g transform="scale(-1,1) translate(-24,0)">
                <path
                  d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                  fill="white"
                />
              </g>
            </svg>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="font-semibold text-black/70 text-lg">
            {isEdit ? t("Edit SFDA Medicine") : t("Create SFDA Medicine")}
          </span>
          {isEdit && item?.supergitMasterCode && (
            <span className="bg-[#0070C2]/10 text-[#0070C2] text-xs font-bold px-2 py-0.5 rounded">
              {item.supergitMasterCode}
            </span>
          )}
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="benefeciary-create">
          {allSections.map((section) =>
            renderSection(section.title, section.fields),
          )}
        </div>

        <div className="flex justify-end gap-2 mt-2 pb-2">
          <AppButton
            label={t("Cancel")}
            onClick={() => navigate("/sfda-medicine")}
            bgColor="#94a3b8"
            startIcon={<CloseIcon />}
          />
          <AppButton
            label={isEdit ? t("Update") : t("Save")}
            type="submit"
            isLoading={creating || updating}
            startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
          />
        </div>
      </form>
    </div>
  );
}

export default SfdaMedicineForm;
